import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdBanner = ({ placement }) => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAds();
  }, [placement]);

  useEffect(() => {
    if (ads.length > 0) {
      // Track impression for current ad
      trackImpression(ads[currentAdIndex]._id);

      // Auto-rotate ads every 5 seconds
      const interval = setInterval(() => {
        setCurrentAdIndex((prev) => (prev + 1) % ads.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [ads, currentAdIndex]);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/advertisements`, {
        params: { placement, status: 'active' }
      });
      setAds(response.data.advertisements || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackImpression = async (adId) => {
    try {
      await axios.post(`${API}/advertisements/${adId}/impression`);
    } catch (error) {
      console.error('Error tracking impression:', error);
    }
  };

  const handleAdClick = async (ad) => {
    try {
      // Track click
      await axios.post(`${API}/advertisements/${ad._id}/click`);
      
      // Navigate to destination
      if (ad.click_url.startsWith('http')) {
        window.open(ad.click_url, '_blank');
      } else {
        navigate(ad.click_url);
      }
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const nextAd = () => {
    setCurrentAdIndex((prev) => (prev + 1) % ads.length);
  };

  const prevAd = () => {
    setCurrentAdIndex((prev) => (prev - 1 + ads.length) % ads.length);
  };

  if (loading || ads.length === 0) {
    return null;
  }

  const currentAd = ads[currentAdIndex];

  return (
    <div className="ad-banner-container mb-8">
      <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden shadow-2xl group">
        {/* Background Image/Video */}
        {currentAd.media_type === 'video' ? (
          <video
            src={currentAd.media_url}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
            style={{ backgroundImage: `url(${currentAd.media_url})` }}
          />
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />

        {/* Content */}
        <div className="relative h-full flex flex-col justify-center px-8 md:px-12">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3 animate-fade-in">
            {currentAd.title}
          </h3>
          {currentAd.description && (
            <p className="text-lg md:text-xl text-gray-200 mb-6 max-w-2xl animate-fade-in-delay">
              {currentAd.description}
            </p>
          )}
          <button
            onClick={() => handleAdClick(currentAd)}
            className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-green-500 hover:text-white transition-all duration-300 w-fit shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Learn More
            <ExternalLink className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation Arrows */}
        {ads.length > 1 && (
          <>
            <button
              onClick={prevAd}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={nextAd}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Indicators */}
        {ads.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
            {ads.map((ad, index) => (
              <button
                key={ad._id || ad.id || `ad-dot-${index}`}
                onClick={() => setCurrentAdIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentAdIndex ? 'bg-white w-8' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Ad Label */}
        <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
          Sponsored
        </div>
      </div>
    </div>
  );
};

export default AdBanner;
